import {
  Box3,
  BufferAttribute,
  BufferGeometry,
  Clock,
  Color,
  DoubleSide,
  EquirectangularReflectionMapping,
  GridHelper,
  Group,
  LineSegments,
  Material,
  MathUtils,
  Mesh,
  MeshBasicMaterial,
  MeshPhongMaterial,
  MeshStandardMaterial,
  Object3D,
  OrthographicCamera,
  Plane,
  Scene,
  ShaderMaterial,
  SphereGeometry,
  Vector2,
  Vector3,
  WebGLRenderer
} from 'three';
import { acceleratedRaycast, computeBoundsTree, disposeBoundsTree, MeshBVH } from 'three-mesh-bvh';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { EdgeSplitModifier } from 'three/examples/jsm/modifiers/EdgeSplitModifier.js';
import { CSS2DObject, CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { GeometryWorkerService, ProcessedGeometry } from '../services/geometry/geometry-worker.service';
// @ts-ignore
import { TrackballControls } from './trackballcontrols';

BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
Mesh.prototype.raycast = acceleratedRaycast;
Object3D.DEFAULT_UP = new Vector3(0, 0, 1);

import { Controls, OutlineWireEffect } from './addons/controls';
import { OrbitPivotManager } from './managers/orbit-pivot.manager';
import { CADEditorVisibilityManager } from './managers/cad-editor-visibility.manager';
import { KeyboardShortcutsManager } from './managers/keyboard-shortcuts.manager';
import { HelperManager } from './managers/helper.manager';
import { DrillBitsManager } from './managers/drill-bits.manager';
import { LoadCaseVisualizationManager } from './managers/load-case-visualization.manager';
import { JawsManager } from './managers/jaws.manager';
import { FPSMonitor } from './managers/fps-monitor';
import { GridManager } from './managers/grid.manager';
import { AssemblyManager } from './managers/assembly.manager';

import * as TWEEN from '@tweenjs/tween.js';
import { DragControls } from 'three/examples/jsm/controls/DragControls.js';
import { AccelerationScene } from './addons/acceleration.scene';
import { ArrowHelper } from './addons/load-helpers/arrowhelper';

import { HttpEventType, HttpRequest } from '@angular/common/http';
import { Easing, Tween } from '@tweenjs/tween.js';
import { BehaviorSubject, Subscription } from 'rxjs';
import { HDRLoader } from 'three/examples/jsm/loaders/HDRLoader.js';
import {
  LoadType,
  ManufacturingMethod,
  PartSubType,
  PartType,
  ProjectType
} from '../models/enums';
import { LoadsGroupItem } from '../models/loadcase.model';
import { SessionService } from '../services/session.service';
import { ThemeService } from '../services/theme/theme.service';
import { UnitConversionService } from '@infinitform/shared-ui';
import { Clipper } from './addons/clipping/clipping';
import { PerFaceMaterial, mergeGeometriesWithFaceIndex } from './addons/per-face-material';
import {
  MULTISAMPLE_COUNT,
  AO_RADIUS,
  AO_INTENSITY,
  AO_SAMPLES,
  DENOISE_SAMPLES,
  DENOISE_RADIUS,
  CAMERA_FRUSTUM_RATIO_WIDE,
  CAMERA_FRUSTUM_RATIO_MEDIUM,
  CAMERA_FRUSTUM_RATIO_NARROW,
  OPTIMIZED_METALNESS,
  OPTIMIZED_REFLECTIVITY,
  OPTIMIZED_ROUGHNESS,
  OPTIMIZED_CLEARCOAT_ROUGHNESS,
  OPTIMIZED_ENVMAP_INTENSITY,
  POLYGON_OFFSET_FACTOR,
  BVH_MAX_LEAF_SIZE,
  WIRE_CUTOFF_ANGLE,
  GENERATED_SHAPE_CUTOFF_ANGLE,
  LOAD_SPHERE_RADIUS,
  LOAD_SPHERE_WIDTH_SEGMENTS,
  LOAD_SPHERE_HEIGHT_SEGMENTS,
  TEXTURE_ANIMATION_INTERVAL_MS,
  DEFAULT_EMPTY_SIZE
} from './engine.constants';
import {
  ProtoAssembly,
  ProtoFace,
  ProtoPart,
  ProtoWire,
  EngineObject,
  FEAResultMeshSet,
  FEAResultInfoV2,
  DimensionsHelper,
  TransformControlEvent
} from './engine.types';
import { Selector } from './addons/selector';
import { FEA_VERTEX_SHADER, FEA_FRAGMENT_SHADER } from './shaders/fea-displacement.shaders';
import { ViewCubeHelper } from './addons/view-cube-helper';
import { calculateCenter } from './helpers';
import { InfiniteGridHelper } from './addons/infinite-grid';

// SSAO Post-processing
// @ts-ignore
import { N8AOPostPass } from 'n8ao';
import { EffectComposer, RenderPass, EffectPass, SMAAEffect, SMAAPreset } from 'postprocessing';

class Engine {
  public envMap = new HDRLoader().setPath('assets/webgl/').load('envmap4.hdr', (texture) => {
    texture.mapping = EquirectangularReflectionMapping;
  });

  public camera!: OrthographicCamera;
  public scene!: Scene;
  public controls!: TrackballControls;
  public renderer!: WebGLRenderer;
  public renderer_css2d!: CSS2DRenderer;

  public outlineEffect!: OutlineWireEffect;

  // SSAO Post-processing
  public composer!: EffectComposer;
  public n8aoPass!: N8AOPostPass;
  public ssaoEnabled = false;

  public gridHelper!: GridHelper;
  public infiniteGridHelper: InfiniteGridHelper | null = null;
  public useInfiniteGrid = true;
  public viewHelper!: ViewCubeHelper;
  public selector!: Selector;
  public accelerationScene!: AccelerationScene;
  public clock: Clock = new Clock();
  private lastTextureAnimTime = 0;
  private readonly textureAnimInterval = TEXTURE_ANIMATION_INTERVAL_MS;

  public objects: EngineObject[] = [];
  public get objectsDesign() {
    return this.objects.filter((o) => o.type === PartType.DESIGN);
  }
  public get objectsNondesign() {
    return this.objects.filter((o) => o.type === PartType.NONDESIGN);
  }

  public group: Group = new Group();
  public groups: Group[] = [];

  public optimizedGroup: Group = new Group();
  public optimizedGroupParametric: Group = new Group();
  public infoGroup: Group = new Group();
  public helperGroup: Group = new Group();

  public symmetryPlanes: { xy?: Group; xz?: Group; yz?: Group } = {};
  public tweens: TWEEN.Tween<any>[] = [];
  // DRILL BITS
  public drillbitMesh: any;
  public drillBits: { x1?: Mesh; y1?: Mesh; z1?: Mesh; x2?: Mesh; y2?: Mesh; z2?: Mesh } = {
    x1: undefined,
    y1: undefined,
    z1: undefined,
    x2: undefined,
    y2: undefined,
    z2: undefined
  };

  public arrowHelper?: Group;
  public arrowHelperTween?: Tween<any>;
  // Z Rotation Arrows for SoftJaws
  public zRotationArrowsGroup: Group = new Group();

  // Orbit pivot manager (extracted)
  public orbitPivot!: OrbitPivotManager;
  public cadEditorVisibility!: CADEditorVisibilityManager;
  public keyboardShortcuts!: KeyboardShortcutsManager;
  public helpers!: HelperManager;
  public drillBitsManager!: DrillBitsManager;
  public loadCaseVisualization!: LoadCaseVisualizationManager;
  public jawsManager!: JawsManager;
  public gridManager!: GridManager;
  public assemblyManager!: AssemblyManager;

  // Selection for focus (F key) - components can set this
  public selectedObjects: Object3D[] = [];

  public keyboardControlPressed = false;

  public helpersVisible = true;

  // Comparison mode flag - when true, windowResize is handled by comparison view
  public comparisonModeActive = false;

  // PROTO
  public assembly!: ProtoAssembly;
  public assemblyOptimized!: Group;
  public assemblyOptimizedParametric!: Group;
  public assemblyOptimizedInfo: FEAResultMeshSet[] = [];
  public currentLoadCase: string | null = null;
  public highlightLoadCase: boolean = false;

  public currentFEAOutput: 'organic' | 'prismatic' = 'organic';

  public progressUpdate = new BehaviorSubject({ total: 0, current: 0 });

  // Dimensions Helper
  public dimensionsHelper!: DimensionsHelper[];
  public dimensionsHelperVisible = false;
  public toggleDimensionsHelper(check?: boolean) {
    if (check) {
      this.dimensionsHelper[0].toggle(this.dimensionsHelperVisible);
      this.dimensionsHelper[1].toggle(this.dimensionsHelperVisible);
      this.dimensionsHelper[2].toggle(this.dimensionsHelperVisible);
      return;
    }
    this.dimensionsHelperVisible = !this.dimensionsHelperVisible;
    this.dimensionsHelper[0].toggle(this.dimensionsHelperVisible);
    this.dimensionsHelper[1].toggle(this.dimensionsHelperVisible);
    this.dimensionsHelper[2].toggle(this.dimensionsHelperVisible);
  }

  public refreshDimensionsHelper(): void {
    this.pivot.updateWorldMatrix(true, true);
    this.group.updateWorldMatrix(true, true);
    const { box } = this.calculateGroupMinMaxCenter(this.group);
    this.dimensionsHelper = Controls.createDimensionsHelper(this, box);
    this.toggleDimensionsHelper(true);
  }

  // Pivot
  public pivot = new Group();

  // Rotation controls for soft jaws
  public rotationControls?: TransformControls;

  // Normal Arrow Helper
  public normalArrowHelper = new ArrowHelper('line', new Vector3(0, 0, 0), new Vector3(0, 0, 40), 0, 0x0000ff);
  public isOptimizationView = false;
  public reactionForces?: boolean = false;
  public animateTexture: boolean = false;
  public deformScale: number = 1;
  public showMaxStressMarker = false;

  // Clipping
  public clipper: Clipper[] = [];
  public clippingEnabled = false;

  public animationFired = false;

  constructor(
    public canvas: HTMLCanvasElement,
    public accelerationCanvas: HTMLCanvasElement,
    public viewHelperContainer: HTMLDivElement,
    public css2dContainer: HTMLDivElement,
    public session: SessionService,
    public themeService: ThemeService,
    public unitConversionService: UnitConversionService
  ) {
    // AssemblyManager must be available before init() since assembly
    // methods (setAssembly, setOptimizedAssembly, etc.) are called
    // by EngineInitializerService before init() runs.
    this.assemblyManager = new AssemblyManager(this);
  }

  async init(projectStatus: number) {
    this.scene = Controls.createScene();
    this.renderer = Controls.createEngine(this.scene, this.canvas);
    this.renderer_css2d = Controls.createCSS2DRenderer(this.css2dContainer, this.canvasWidth, this.canvasHeight);
    this.camera = Controls.createCamera(this.scene);
    this.controls = Controls.createTrackballControls(this.camera, this.canvas, this.scene);
    this.orbitPivot = new OrbitPivotManager(this);
    this.cadEditorVisibility = new CADEditorVisibilityManager(this);
    this.helpers = new HelperManager(this);
    this.drillBitsManager = new DrillBitsManager(this);
    this.loadCaseVisualization = new LoadCaseVisualizationManager(this);
    this.jawsManager = new JawsManager(this);
    this.gridManager = new GridManager(this);
    this.controls.addEventListener('start', this.orbitPivot.controlsActionStartHandler);
    this.controls.addEventListener('end', this.orbitPivot.controlsActionEndHandler);

    await this.initMesh();

    if (this.session.project.projectType === ProjectType.TopologyOptimization) {
      if (projectStatus === 4) {
        if (this.session.organicAssemblyAvailable) {
          this.initOptimizedMesh();
        }
        if (this.session.parametricAssemblyAvailable) {
          this.initOptimizedMeshParametric();
        }
      }
    }

    const { center, size, box } = this.objectInfo;
    this.scene.add(this.infoGroup);
    this.scene.add(this.helperGroup);
    this.controls.target.copy(center);

    Controls.fitCameraToCenteredObject(this.camera, size, center);
    this.orbitPivot.init(size, center);

    this.selector = Controls.createRaycaster(this.camera, this.canvas, this.groups, this.group, this.objects, this);
    this.viewHelper = Controls.createCameraCubeControls(this, this.viewHelperContainer);
    this.outlineEffect = Controls.createOutlineEffectWithLineMaterial(this.session);
    this.accelerationScene = new AccelerationScene(this.accelerationCanvas);
    this.gridHelper = Controls.createGridHelper(this.gridHelper, size, this.scene, this.canvas, center);
    if (this.useInfiniteGrid && this.gridHelper) {
      this.gridHelper.visible = false;
      this.gridHelper.userData['xLine'].visible = false;
      this.gridHelper.userData['yLine'].visible = false;
      this.gridHelper.userData['groundPlane'].visible = false;
    }
    this.createInfiniteGrid(size, center);

    this.drillBitsManager.generateSymmetryPlanes(Math.max(size.x, size.y, size.z), center, size);

    this.dimensionsHelper = Controls.createDimensionsHelper(this, box);

    if (this.session.project.projectType === ProjectType.TopologyOptimization) {
      await this.drillBitsManager.initDrillBits();
    }

    this.scene.add(this.pivot);
    this.pivot.position.set(this.objectInfo.center.x, this.objectInfo.center.y, this.objectInfo.center.z);

    this.pivot.attach(this.group);

    if (this.session.project.projectType === ProjectType.SoftJaws) {
      this.scene.add(this.jaws.jawsPivot);
      this.jaws.jawsPivot.position.set(this.objectInfo.center.x, this.objectInfo.center.y, this.objectInfo.center.z);
      // Result: X,Y from model center (pivot offset), Z at 0 (binary output has correct absolute Z)
      this.jaws.result.position.set(this.objectInfo.center.x, this.objectInfo.center.y, 0);
      this.scene.add(this.jaws.result);
    }

    this.normalArrowHelper.visible = false;
    this.scene.add(this.normalArrowHelper);

    this.session.project.userCoordinateSystem.origin = [
      parseFloat(this.objectInfo.center.x.toFixed(4)),
      parseFloat(this.objectInfo.center.y.toFixed(4)),
      parseFloat(this.objectInfo.center.z.toFixed(4))
    ];

    if (this.session.project.userCoordinateSystem.enabled) {
      setTimeout(() => {
        this.ucsUpdate();
      });
    }

    this.themeSubscription = this.themeService.appliedTheme$.subscribe((theme) => {
      setTimeout(() => {
        this.applyThemeToGrid();
        this.viewHelper?.setTheme(theme === 'dark');
      });
    });

    this.renderer.setAnimationLoop(() => this.animate());

    // Initialize keyboard shortcuts (F = focus, H = home)
    this.keyboardShortcuts = new KeyboardShortcutsManager(this, this.orbitPivot);
    this.keyboardShortcuts.init();

    // Mouse Bindings
    setTimeout(() => {
      this.session.initMousePreset();
      this.session.initGraphicsPreset();
      this.windowResize();
    });

    // Initialize SSAO post-processing
    this.initSSAO();
  }

  private initSSAO(): void {
    const pixelRatio = this.renderer.getPixelRatio();
    const width = this.canvasWidth * pixelRatio;
    const height = this.canvasHeight * pixelRatio;

    // Ensure scene has a background color (prevents gray background with SSAO)
    const theme = this.themeService.getAppliedTheme();
    this.scene.background = new Color(theme === 'dark' ? '#191c22' : '#ffffff');

    // Create effect composer with multisampling
    this.composer = new EffectComposer(this.renderer, {
      frameBufferType: undefined,
      multisampling: MULTISAMPLE_COUNT
    });

    // Add render pass
    const renderPass = new RenderPass(this.scene, this.camera);
    this.composer.addPass(renderPass);

    // Create N8AO pass
    this.n8aoPass = new N8AOPostPass(this.scene, this.camera, width, height);
    this.n8aoPass.configuration.aoRadius = AO_RADIUS;
    this.n8aoPass.configuration.distanceFalloff = 1.0;
    this.n8aoPass.configuration.intensity = AO_INTENSITY;
    this.n8aoPass.configuration.aoSamples = AO_SAMPLES;
    this.n8aoPass.configuration.denoiseSamples = DENOISE_SAMPLES;
    this.n8aoPass.configuration.denoiseRadius = DENOISE_RADIUS;
    this.n8aoPass.configuration.halfRes = true; // Render AO at half resolution
    this.n8aoPass.setDisplayMode('Combined');

    this.composer.addPass(this.n8aoPass);

    // Add SMAA pass to smooth alpha hash noise
    const smaaEffect = new SMAAEffect({ preset: SMAAPreset.HIGH });
    const smaaPass = new EffectPass(this.camera, smaaEffect);
    this.composer.addPass(smaaPass);

    // Temporary: force AO off regardless of user settings.
    this.ssaoEnabled = false;
  }

  // ── Backward-compatible delegates to OrbitPivotManager ──────────────
  public showTemporaryPivot(): void { this.orbitPivot.showTemporary(); }
  public hideTemporaryPivot(): void { this.orbitPivot.hideTemporary(); }
  public clearOrbitPivot(): void { this.orbitPivot.clear(); }
  public resetOrbitPivot(): void { this.orbitPivot.reset(); }
  public refreshOrbitPivotHint(): void { this.orbitPivot.refreshHint(); }

  // ── Backward-compatible delegates to KeyboardShortcutsManager ──────

  public get gridVisible(): boolean { return this.gridManager.visible; }
  public set gridVisible(value: boolean) { this.gridManager.visible = value; }
  public toggleGrid(): void { this.gridManager.toggle(); }
  private createInfiniteGrid(size: Vector3, center: Vector3): void { this.gridManager.createInfiniteGrid(size, center); }
  private disposeInfiniteGrid(): void { this.gridManager.disposeInfiniteGrid(); }

  public focusSelection(): void { this.keyboardShortcuts.focusSelection(); }
  public fitAll(): void { this.keyboardShortcuts.fitAll(); }
  public resetHomeView(): void { this.keyboardShortcuts.resetHomeView(); }
  public setSelectedObjects(objects: Object3D[]): void { this.selectedObjects = objects; }

  public clippingPlanes = [new Plane(), new Plane(), new Plane()];
  public initClipping(): void {
    const prevClipperPositions = this.clipper[0]
      ? {
          x: this.clipper[0].planePositions.x,
          y: this.clipper[0].planePositions.y,
          z: this.clipper[0].planePositions.z,
        }
      : null;

    const isStaleClipperObject = (o: Object3D): boolean =>
      o.name === 'PlaneMeshX' || o.name === 'PlaneMeshY' || o.name === 'PlaneMeshZ' ||
      o.name.endsWith('_SurfaceModel') || o.name.endsWith('_BackSideModel') ||
      o.name.endsWith('_StencilFront') || o.name.endsWith('_StencilBack') ||
      o.name.endsWith('_Collider');

    // `_Collider` excluded — must stay hidden (basic white material that would paint over the model).
    const isClippingVisibilityTarget = (o: Object3D): boolean =>
      o.name === 'PlaneMeshX' || o.name === 'PlaneMeshY' || o.name === 'PlaneMeshZ' ||
      o.name.endsWith('_SurfaceModel') || o.name.endsWith('_BackSideModel') ||
      o.name.endsWith('_StencilFront') || o.name.endsWith('_StencilBack');

    const staleObjects: Object3D[] = [];
    this.scene.traverse((o) => {
      if (isStaleClipperObject(o)) staleObjects.push(o);
    });
    const disposeMeshResources = (o: Object3D) => {
      (o as { geometry?: { dispose?: () => void } }).geometry?.dispose?.();
      const mat = (o as { material?: unknown }).material;
      if (mat && typeof (mat as { dispose?: () => void }).dispose === 'function') {
        (mat as { dispose: () => void }).dispose();
      }
    };
    for (const obj of staleObjects) {
      obj.removeFromParent();
      disposeMeshResources(obj);
    }
    if (this.clipper.length > 0) {
      for (const c of this.clipper) {
        for (const line of [c.outlineLinesX, c.outlineLinesY, c.outlineLinesZ]) {
          if (!line) continue;
          line.removeFromParent();
          line.geometry?.dispose?.();
          const lineMat = (line as { material?: unknown }).material;
          if (lineMat && typeof (lineMat as { dispose?: () => void }).dispose === 'function') {
            (lineMat as { dispose: () => void }).dispose();
          }
        }
      }
      this.clipper = [];
    }

    const clipper = new Clipper(this.scene, this.clippingPlanes);
    let usedSetObject = false;

    // Phase 1: Process optimized meshes and wire lines
    this.scene.traverse((o) => {
      if (o instanceof Mesh && (o.name === 'OptimizedOrganicMesh' || o.name === 'OptimizedPrismaticMesh')) {
        if (!usedSetObject) {
          clipper.setObject(o);
          usedSetObject = true;
        } else {
          o.material.clippingPlanes = this.clippingPlanes;
          clipper.addObject(o);
        }
      } else if (
        o instanceof Line2 &&
        o.userData &&
        (o.userData['line_type'] === 'OptimizedOrganicMeshLine' ||
          o.userData['line_type'] === 'OptimizedPrismaticMeshLine' ||
          o.userData['line_type'] === 'AssemblyMeshLine')
      ) {
        o.material.clippingPlanes = this.clippingPlanes;
      }
    });

    // Assembly + FEA meshes are non-manifold — skip stencil cap, keep edge detection.
    for (const obj of this.objects) {
      let mergedMesh: Mesh | undefined;
      obj.group.traverse((child: Object3D) => {
        if (child instanceof Mesh && child.userData?.['isMergedMesh']) {
          mergedMesh = child;
        }
      });
      if (mergedMesh) {
        clipper.addObject(mergedMesh, false);
      }
    }

    this.infoGroup.children.forEach((child) => {
      if (child instanceof Mesh && child.material) {
        (child.material as MeshBasicMaterial).clippingPlanes = this.clippingPlanes;
        clipper.addChildObject(child, false);
      }
    });

    this.clipper = [clipper];

    // Recompute all BVH asynchronously
    clipper.computeAllBVHAsync(this.geometryWorker).catch(() => {});

    // Set plane positions based on overall object bounds
    const { box } = this.objectInfo;
    clipper.setMinMax(
      box.min.x - 10,
      box.max.x + 10,
      box.min.y - 10,
      box.max.y + 10,
      box.min.z - 10,
      box.max.z + 10
    );
    // Slider values are offsets from the model mid-plane (0 = centre,
    // ± = either side). Default is 0 from setMinMax; only override when
    // restoring a previous slider position across re-init.
    if (prevClipperPositions) {
      clipper.setPosition(prevClipperPositions.x, 'x');
      clipper.setPosition(prevClipperPositions.y, 'y');
      clipper.setPosition(prevClipperPositions.z, 'z');
    }

    this.renderer.localClippingEnabled = this.clippingEnabled;
    this.scene.traverse((o) => {
      if (isClippingVisibilityTarget(o)) {
        o.visible = this.clippingEnabled;
      }
    });
    if (clipper.outlineLinesX) clipper.outlineLinesX.visible = this.clippingEnabled;
    if (clipper.outlineLinesY) clipper.outlineLinesY.visible = this.clippingEnabled;
    if (clipper.outlineLinesZ) clipper.outlineLinesZ.visible = this.clippingEnabled;
  }

  public ucsUpdate() {
    if (this.session.project.userCoordinateSystem.enabled) {
      this.session.engine?.invalidateCachedObjectInfo();
      const { rotation } = this.session.project.userCoordinateSystem;
      const rX = MathUtils.degToRad(rotation[0]);
      const rY = MathUtils.degToRad(rotation[1]);
      const rZ = MathUtils.degToRad(rotation[2]);

      this.session.engine!.pivot.rotateOnWorldAxis(new Vector3(1, 0, 0), rX);
      this.session.engine!.pivot.rotateOnWorldAxis(new Vector3(0, 1, 0), rY);
      this.session.engine!.pivot.rotateOnWorldAxis(new Vector3(0, 0, 1), rZ);
    }
    this.gridHelper = Controls.createGridHelper(
      this.gridHelper,
      this.objectInfo.size,
      this.scene,
      this.canvas,
      this.objectInfo.center
    );
    if (this.useInfiniteGrid && this.gridHelper) {
      this.gridHelper.visible = false;
      this.gridHelper.userData['xLine'].visible = false;
      this.gridHelper.userData['yLine'].visible = false;
      this.gridHelper.userData['groundPlane'].visible = false;
    }
    this.createInfiniteGrid(this.objectInfo.size, this.objectInfo.center);
    this.applyThemeToGrid(); // Apply theme to recreated grid
    this.dimensionsHelper = Controls.createDimensionsHelper(this, this.objectInfo.box);
    this.toggleDimensionsHelper(true);
    if (
      this.isOptimizationView &&
      (this.session.project.manufacturingMethod === ManufacturingMethod.InjectionMoldingWithDrawDirection ||
        this.session.project.manufacturingMethod === ManufacturingMethod.DieCasting)
    ) {
      this.session.engine?.drawDirectionVectorChange(undefined);
    } else {
      this.session.engine?.disposeArrow();
    }
  }

  // ── Backward-compatible delegates to HelperManager ─────────────────
  public drawDirectionVectorChange(direction?: 'xp' | 'yp' | 'zp' | 'xm' | 'ym' | 'zm'): void { this.helpers.drawDirectionVectorChange(direction); }

  windowResize(e?: Event): void {
    if (!this.animationFired) return;
    // Skip if comparison mode is active - comparison view handles its own resize
    if (this.comparisonModeActive) return;
    try {
      let canvasWidth = this.canvasWidth;
      let canvasHeight = this.canvasHeight;

      let ratio = 1;
      if (this.session.currentGraphicsSettings) {
        if (this.session.currentGraphicsSettings.UPSCALING === 1) {
          ratio = CAMERA_FRUSTUM_RATIO_WIDE;
        } else if (this.session.currentGraphicsSettings.UPSCALING === 2) {
          ratio = CAMERA_FRUSTUM_RATIO_MEDIUM;
        }
        if (this.session.currentGraphicsSettings.UPSCALING === 3) {
          ratio = CAMERA_FRUSTUM_RATIO_NARROW;
        }

        // Temporary: force AO off regardless of user settings.
        this.ssaoEnabled = false;
        canvasWidth *= ratio;
        canvasHeight *= ratio;
      }

      const aspect = canvasWidth / canvasHeight;
      const { size } = this.objectInfo;
      const frustumSize = Math.max(size.x, size.y, size.z);

      const width = canvasWidth > canvasHeight ? frustumSize / 2 : (frustumSize * aspect) / 2;
      const height = canvasWidth > canvasHeight ? frustumSize / aspect / 2 : frustumSize / 2;

      this.camera.right = width * 2;
      this.camera.left = -width * 2;
      this.camera.top = height * 2;
      this.camera.bottom = -height * 2;
      this.camera.updateProjectionMatrix();

      this.renderer.setSize(canvasWidth, canvasHeight, false);

      // Resize SSAO composer with proper pixel ratio
      if (this.composer) {
        const pixelRatio = this.renderer.getPixelRatio();
        this.composer.setSize(canvasWidth, canvasHeight);
        if (this.n8aoPass) {
          this.n8aoPass.setSize(canvasWidth * pixelRatio, canvasHeight * pixelRatio);
        }
      }

      this.renderer_css2d.setSize(canvasWidth, canvasHeight);
      this.renderer_css2d.domElement.style.transform = `scale(${1 / window.devicePixelRatio / ratio})`;
      this.renderer_css2d.domElement.style.transformOrigin = 'top left';

      this.objects.forEach((obj) => obj.wirematerial.resolution.set(canvasWidth, canvasHeight));

      // Note: No need for requestAnimationFrame here - setAnimationLoop already handles render loop
    } catch (e) {}
  }

  /**
   * Render one frame at high resolution (default 3840px long edge — 4K) and
   * return it as a PNG data URL. Only the drawing buffer is resized (CSS size
   * untouched, `updateStyle: false`), and both dimensions scale by the same
   * factor so the orthographic frustum needs no change. The renderer is
   * created without `preserveDrawingBuffer`, so the capture must happen
   * synchronously right after the explicit render. The view gizmo and CSS2D
   * annotations are intentionally not rendered into the capture.
   */
  captureScreenshot(targetLongEdge = 3840): string | null {
    if (!this.renderer || !this.camera || !this.scene) return null;

    const prevPixelRatio = this.renderer.getPixelRatio();
    const prevSize = this.renderer.getSize(new Vector2());
    const longCssEdge = Math.max(prevSize.x, prevSize.y);
    if (longCssEdge === 0) return null;

    // Never downscale below the current buffer, never exceed what the GPU
    // can allocate as a render target.
    const maxTextureSize = this.renderer.capabilities.maxTextureSize ?? 4096;
    const currentBufferLongEdge = longCssEdge * prevPixelRatio;
    const longEdge = Math.min(
      Math.max(targetLongEdge, currentBufferLongEdge),
      maxTextureSize,
    );
    const scale = longEdge / longCssEdge;
    const width = Math.round(prevSize.x * scale);
    const height = Math.round(prevSize.y * scale);

    try {
      this.renderer.setPixelRatio(1);
      this.renderer.setSize(width, height, false);
      if (this.composer) this.composer.setSize(width, height);
      if (this.n8aoPass) this.n8aoPass.setSize(width, height);
      this.objects.forEach((obj) => obj.wirematerial.resolution.set(width, height));

      // Mirror the animation loop's render path (autoClear is off).
      this.renderer.clear();
      if (this.ssaoEnabled && this.composer) {
        this.composer.render();
      } else {
        this.renderer.render(this.scene, this.camera);
      }

      return this.renderer.domElement.toDataURL('image/png');
    } catch (e) {
      console.error('[Engine] captureScreenshot failed:', e);
      return null;
    } finally {
      this.renderer.setPixelRatio(prevPixelRatio);
      this.renderer.setSize(prevSize.x, prevSize.y, false);
      if (this.composer) this.composer.setSize(prevSize.x, prevSize.y);
      if (this.n8aoPass) {
        this.n8aoPass.setSize(prevSize.x * prevPixelRatio, prevSize.y * prevPixelRatio);
      }
      this.objects.forEach((obj) =>
        obj.wirematerial.resolution.set(prevSize.x, prevSize.y),
      );
    }
  }

  async decodeAssemblyProto(buffer: ArrayBuffer): Promise<ProtoAssembly> {
    return this.assemblyManager.decodeAssemblyProto(buffer);
  }
  async setAssembly(assembly: ArrayBuffer): Promise<void> { return this.assemblyManager.setAssembly(assembly); }
  async setOptimizedAssembly(assembly: ArrayBuffer): Promise<void> { return this.assemblyManager.setOptimizedAssembly(assembly); }
  async setOptimizedAssemblyParametric(assembly: ArrayBuffer): Promise<void> { return this.assemblyManager.setOptimizedAssemblyParametric(assembly); }
  setOptimizedInfo(info: FEAResultInfoV2 | FEAResultMeshSet[]): void { this.assemblyManager.setOptimizedInfo(info); }

  public vertexShader = FEA_VERTEX_SHADER;
  public fragmentShader = FEA_FRAGMENT_SHADER;

  public optimizeInfoFlowVersion2: FEAResultInfoV2 = { organic: [] };

  /**
   * Helper to get geometry worker service
   */
  private get geometryWorker(): GeometryWorkerService {
    return this.session.geometryWorker;
  }

  /**
   * Compute BVH for a geometry using web worker.
   * Falls back to synchronous computation if worker is unavailable.
   */
  async computeBVHAsync(geometry: BufferGeometry, options?: { maxLeafSize?: number }): Promise<void> {
    const positionAttr = geometry.getAttribute('position');
    const indexAttr = geometry.getIndex();

    if (!positionAttr || !indexAttr) {
      // Fallback to sync for non-indexed geometry
      geometry.computeBoundsTree();
      return;
    }

    // Try worker first
    if (this.geometryWorker?.isSupported) {
      try {
        const positions = positionAttr.array as Float32Array;
        const indices = indexAttr.array as Uint32Array;

        const result = await this.geometryWorker.computeBVH(positions, indices, {
          maxLeafSize: options?.maxLeafSize ?? BVH_MAX_LEAF_SIZE
        });

        // Deserialize and attach to geometry
        const bvh = MeshBVH.deserialize(result.serializedBVH, geometry);
        geometry.boundsTree = bvh;
        return;
      } catch (e) {
        console.warn('Worker BVH computation failed, falling back to sync:', e);
      }
    }

    // Fallback to synchronous computation
    geometry.computeBoundsTree();
  }

  /**
   * Convert ProcessedGeometry from worker to Three.js BufferGeometry
   */
  private processedGeometryToBuffer(processed: ProcessedGeometry): BufferGeometry {
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(processed.positions, 3));
    geometry.setIndex(new BufferAttribute(processed.indices, 1));
    geometry.setAttribute('normal', new BufferAttribute(processed.normals, 3));
    geometry.computeBoundingSphere();
    geometry.computeBoundingBox();
    return geometry;
  }

  /**
   * Process a single face geometry - uses worker if available, falls back to main thread
   */
  private async processFaceGeometry(
    vertices: Float32Array | number[],
    indices: Uint32Array | number[],
    cutOffAngle: number = WIRE_CUTOFF_ANGLE
  ): Promise<BufferGeometry> {
    // Try worker first
    if (this.geometryWorker?.isSupported) {
      try {
        const processed = await this.geometryWorker.processGeometry(
          vertices instanceof Float32Array ? vertices : new Float32Array(vertices),
          indices instanceof Uint32Array ? indices : new Uint32Array(indices),
          cutOffAngle
        );
        return this.processedGeometryToBuffer(processed);
      } catch (e) {
        console.warn('Worker geometry processing failed, falling back to main thread:', e);
      }
    }

    // Fallback to main thread
    const baseGeometry = new BufferGeometry();
    baseGeometry.setAttribute(
      'position',
      new BufferAttribute(vertices instanceof Float32Array ? vertices : new Float32Array(vertices), 3)
    );
    baseGeometry.setIndex(
      new BufferAttribute(indices instanceof Uint32Array ? indices : new Uint32Array(indices), 1)
    );

    const edgeSplit = new EdgeSplitModifier();
    const cutOffRad = (cutOffAngle * Math.PI) / 180;
    const geometry = mergeVertices(edgeSplit.modify(baseGeometry, cutOffRad, false));
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();
    geometry.computeBoundingBox();
    return geometry;
  }

  createWireLineMaterial(): LineMaterial {
    const material = new LineMaterial({
      color: new Color('rgb(0,0,0)'),
      linewidth: 1,
      dashed: false,
      precision: 'highp',
      polygonOffset: true,
      polygonOffsetFactor: POLYGON_OFFSET_FACTOR,
      clippingPlanes: this.clippingPlanes
    });
    material.resolution.set(
      this.canvas.clientWidth * window.devicePixelRatio,
      this.canvas.clientHeight * window.devicePixelRatio
    );
    return material;
  }

  createMergedWireGroup(
    wires: ProtoWire[],
    wirematerial: LineMaterial,
    lineType: string
  ): { wiresGroup: Group; wireNames: string[] } {
    const wiresGroup = new Group();
    wiresGroup.name = 'wires';
    const wireNames: string[] = [];
    const allSegmentPositions: number[] = [];

    for (const wireValues of wires) {
      const wire = wireValues.geometry as number[];
      for (let j = 0; j < wire.length - 3; j += 3) {
        const x0 = wire[j], y0 = wire[j + 1], z0 = wire[j + 2];
        const x1 = wire[j + 3], y1 = wire[j + 4], z1 = wire[j + 5];
        if (Math.abs(x0) > 1e38 || Math.abs(y0) > 1e38 || Math.abs(z0) > 1e38 ||
            Math.abs(x1) > 1e38 || Math.abs(y1) > 1e38 || Math.abs(z1) > 1e38) continue;
        allSegmentPositions.push(x0, y0, z0, x1, y1, z1);
      }
    }

    if (allSegmentPositions.length > 0) {
      const mergedWireGeometry = new LineSegmentsGeometry();
      mergedWireGeometry.setPositions(allSegmentPositions);
      const mergedLine = new LineSegments2(mergedWireGeometry, wirematerial);
      mergedLine.computeLineDistances();
      mergedLine.userData['line_type'] = lineType;
      mergedLine.scale.set(1, 1, 1);
      mergedLine.name = mergedLine.uuid;
      wireNames.push(mergedLine.name);
      wiresGroup.add(mergedLine);
    }

    return { wiresGroup, wireNames };
  }

  async initMesh(): Promise<void> {
    this.progressUpdate.next({ total: this.assembly.parts.length, current: 1 });
    const cutOffAngle = WIRE_CUTOFF_ANGLE;

    // Process all parts - use worker pool for parallel processing if available
    const processPartAsync = async (obj: ProtoPart, partIndex: number) => {
      const group = new Group();
      group.name = obj.name;
      group.userData = { id: obj.id, pid: obj.parentId };

      // Process all face geometries in parallel using worker
      const faceGeometries: BufferGeometry[] = [];
      const faceIds: string[] = [];

      // Process faces in parallel batches
      const facePromises = obj.faces.map(async (face: ProtoFace) => {
        const geometry = await this.processFaceGeometry(
          new Float32Array(face.vertices),
          new Uint32Array(face.indices),
          cutOffAngle
        );
        return { geometry, faceId: face.id };
      });

      const processedFaces = await Promise.all(facePromises);
      for (const { geometry, faceId } of processedFaces) {
        faceGeometries.push(geometry);
        faceIds.push(faceId);
      }
      // Merge all face geometries into one with faceIndex attribute
      const { geometry: mergedGeometry, faceIdToIndex } = mergeGeometriesWithFaceIndex(faceGeometries, faceIds);

      // Create per-face material that extends MeshPhysicalMaterial
      const perFaceMaterial = new PerFaceMaterial(
        {
          color: obj.color,
          side: DoubleSide,
          vertexColors: false,
          flatShading: false,
          opacity: 1,
          transparent: false,
          metalness: 0.7,
          reflectivity: 0.6,
          roughness: 0.5,
          clearcoat: 1,
          clearcoatRoughness: OPTIMIZED_CLEARCOAT_ROUGHNESS,
          envMap: this.envMap,
          envMapIntensity: 0.4,
          clippingPlanes: this.clippingPlanes
        },
        faceIds,
        obj.color
      );

      // Create single mesh for all faces
      const meshobj = new Mesh(mergedGeometry, perFaceMaterial);
      // Compute BVH asynchronously in worker
      this.computeBVHAsync(meshobj.geometry).catch(() => {});
      meshobj.name = meshobj.uuid;
      meshobj.userData = {
        id: obj.id,
        parentId: obj.parentId,
        originalColor: obj.color,
        faceIds: faceIds,
        faceIdToIndex: faceIdToIndex,
        perFaceMaterial: perFaceMaterial,
        isMergedMesh: true
      };

      group.add(meshobj);

      // Store reference to faceIds for selector compatibility
      const faces = faceIds;

      const wirematerial = this.createWireLineMaterial();

      const { wiresGroup, wireNames: wires } = this.createMergedWireGroup(obj.wires, wirematerial, 'AssemblyMeshLine');
      group.add(wiresGroup);

      this.createPartHelpers(group);

      return {
        group,
        data: obj,
        faces,
        wires,
        wirematerial
      };
    };

    // Process parts sequentially to maintain order and update progress
    // (parallel processing of faces within each part still happens)
    for (let i = 0; i < this.assembly.parts.length; i++) {
      const obj = this.assembly.parts[i];
      const partData = await processPartAsync(obj, i);
      
      this.group.add(partData.group);
      this.groups.push(partData.group);
      this.objects.push(partData);
      this.progressUpdate.next({ total: this.assembly.parts.length, current: i + 1 });
    }

    this.objects.sort(function (a, b) {
      if (a.data.name < b.data.name) {
        return -1;
      }
      if (a.data.name > b.data.name) {
        return 1;
      }
      return 0;
    });

    // Apply custom part names if they exist
    if (this.session.project?.partNames) {
      this.updatePartNames(this.session.project.partNames);
    }

    this.scene.add(this.group);
  }

  private createPartHelpers(group: Group): void {
    const createArrow = (
      name: string,
      type: 'line' | 'circle',
      dir: Vector3,
      color: number,
      center: Vector3
    ): ArrowHelper => {
      const start = new Vector3(0, 0, 40);
      const end = new Vector3(0, 0, 0);
      const direction = start.clone().sub(end);
      const length = direction.length();
      const arrow = new ArrowHelper(type, direction.normalize(), end, length, color);
      arrow.setDirection(dir);
      arrow.name = name;
      arrow.position.copy(center);
      arrow.layers.set(1);
      this.helperGroup.add(arrow);
      return arrow;
    };

    const createLinkLine = (to: Vector3, from: Vector3, color: number = 0x000000): Line2 => {
      const geometry = new LineGeometry();
      geometry.setPositions([from.x, from.y, from.z, to.x, to.y, to.z]);
      const matLine = new LineMaterial({ color: color, linewidth: 3, dashed: true });
      const line = new Line2(geometry, matLine);
      line.computeLineDistances();
      line.scale.set(1, 1, 1);
      matLine.resolution.set(this.canvasWidth, this.canvasHeight);
      line.layers.set(1);
      this.helperGroup.add(line);
      return line;
    };

    const createDragControl = (
      name: string,
      color: number,
      center: Vector3,
      partArrow: ArrowHelper,
      partMoment: ArrowHelper,
      partLink: Line2
    ): Mesh => {
      const geometry = new SphereGeometry(LOAD_SPHERE_RADIUS, LOAD_SPHERE_WIDTH_SEGMENTS, LOAD_SPHERE_HEIGHT_SEGMENTS);
      const material = new MeshPhongMaterial({
        color: color,
        emissive: 0x000000,
        depthTest: false,
        alphaTest: 0,
        transparent: true
      });
      const sphere = new Mesh(geometry, material);
      sphere.name = name;
      sphere.position.copy(center);
      sphere.layers.set(1);
      this.helperGroup.add(sphere);

      const controls = new DragControls([sphere], this.camera, this.canvas);
      controls.enabled = false;

      controls.addEventListener('hoveron', (event) => {
        this.selector.isMouseMoveHalted = true;
        this.selector.unhighlightPart();
        const obj = event.object as Mesh;
        const mat = obj?.material as MeshStandardMaterial;
        if (mat?.emissive) mat.emissive.setHex(0x999999);
      });

      controls.addEventListener('hoveroff', (event) => {
        this.selector.isMouseMoveHalted = false;
        const obj = event.object as Mesh;
        const mat = obj?.material as MeshStandardMaterial;
        if (mat?.emissive) mat.emissive.setHex(0x000000);
      });

      controls.addEventListener('dragstart', (event) => {
        this.selector.isMouseDownHalted = true;
        this.selector.unhighlightPart();
        const obj = event.object as Mesh;
        const mat = obj?.material as MeshStandardMaterial;
        if (mat?.emissive) mat.emissive.setHex(0x999999);
      });

      controls.addEventListener('drag', (event) => {
        if (partArrow && partMoment) {
          partArrow.position.copy(event.object.position);
          partMoment.position.copy(event.object.position);
          const lineGeometry = new LineGeometry();
          const ctr = new Box3().setFromObject(group).getCenter(new Vector3());
          ctr.applyMatrix4(group.matrixWorld.clone().invert());
          group.localToWorld(ctr);
          lineGeometry.setPositions([
            ctr.x, ctr.y, ctr.z,
            event.object.position.x, event.object.position.y, event.object.position.z
          ]);
          partLink?.geometry.copy(lineGeometry);
          partLink?.computeLineDistances();
          partLink?.scale.set(1, 1, 1);
        }
        this.selector.moved.emit(event.object.position);
      });

      controls.addEventListener('dragend', (event) => {
        this.selector.isMouseDownHalted = false;
        const obj = event.object as Mesh;
        const mat = obj?.material as MeshStandardMaterial;
        if (mat?.emissive) mat.emissive.setHex(0x000000);
        this.selector.moved.emit(event.object.position);
      });

      sphere.userData['dragControl'] = controls;
      return sphere;
    };

    const createReactionForceAnnotation = (
      name: string,
      center: Vector3
    ): {
      annotation: CSS2DObject;
      setAnnotationText: (x: string, y: string, z: string, magnitude: string, unit: string) => void;
    } => {
      const div = document.createElement('div');
      div.className = 'annotation-container';
      const annotation = new CSS2DObject(div);
      annotation.name = name;
      annotation.position.copy(center);
      annotation.visible = false;
      this.scene.add(annotation);

      const setAnnotationText = (x: string, y: string, z: string, magnitude: string, unit: string): void => {
        div.textContent = '';
        const container = document.createElement('div');
        container.className = 'annotation-container';
        const content = document.createElement('div');
        content.className = 'annotation-dimension-content reaction-force-annotation';
        content.style.display = 'flex';
        content.style.flexDirection = 'column';
        content.style.gap = '1px';
        // Magnitude on top — what the user reads first
        const magSpan = document.createElement('span');
        magSpan.style.fontWeight = '600';
        magSpan.textContent = `|F| = ${magnitude} ${unit}`;
        content.appendChild(magSpan);
        // Components below
        for (const [label, val] of [['X', x], ['Y', y], ['Z', z]]) {
          const span = document.createElement('span');
          span.textContent = `${label}: ${val} ${unit}`;
          content.appendChild(span);
        }
        container.appendChild(content);
        div.appendChild(container);
      };
      return { annotation, setAnnotationText };
    };

    // Reaction-force arrow: a 3D arrow at the part center pointing in the
    // reaction-force direction, length scaled by magnitude. Created hidden;
    // visibility + direction + length are set when the toggle fires.
    // Color is amber/orange (0xff8800) to distinguish from applied loads
    // (blue 0x0000ff for force, red 0xff0000 for moment).
    const createReactionForceArrow = (name: string, center: Vector3): ArrowHelper => {
      const arrowLength = 40;
      const arrow = new ArrowHelper('line', new Vector3(0, 0, 1), new Vector3(), arrowLength, 0xff8800);
      arrow.name = name;
      arrow.position.copy(center);
      arrow.visible = false;
      // ArrowHelper constructor puts line + cone on layer 1 (hidden). Override
      // both children to layer 0 so the camera renders them once visible=true.
      arrow.layers.set(0);
      arrow.line.layers.set(0);
      arrow.cone.layers.set(0);
      this.scene.add(arrow);
      return arrow;
    };

    group.updateWorldMatrix(true, true);
    group.updateMatrixWorld(true);
    group.updateMatrix();

    const center = new Box3().setFromObject(group).getCenter(new Vector3());
    center.applyMatrix4(group.matrixWorld.clone().invert());
    group.localToWorld(center);

    group.userData['part_arrow'] = createArrow('part_arrow', 'line', new Vector3(0, 0, 0), 0x0000ff, center);
    group.userData['part_moment'] = createArrow('part_moment', 'circle', new Vector3(0, 0, 0), 0xff0000, center);
    group.userData['part_link_line'] = createLinkLine(center, center, 0x000000);
    group.userData['part_sphere'] = createDragControl(
      'part_sphere', 0xff0000, center,
      group.userData['part_arrow'],
      group.userData['part_moment'],
      group.userData['part_link_line']
    );
    group.userData['part_reaction_force_annotation'] = createReactionForceAnnotation(
      'part_reaction_force_annotation', center
    );
    group.userData['part_reaction_force_arrow'] = createReactionForceArrow(
      'part_reaction_force_arrow', center
    );
  }

  initOptimizedMesh() {
    this.optimizedGroup.visible = true;
    this.optimizedGroup.add(this.assemblyOptimized);
    this.scene.add(this.optimizedGroup);
  }

  updatePartNames(partNames: { [partId: string]: string }) {
    this.objects.forEach((obj) => {
      if (partNames[obj.data.id]) {
        obj.data.name = partNames[obj.data.id];
        obj.group.name = partNames[obj.data.id];
      }
    });
  }

  initOptimizedMeshParametric() {
    this.optimizedGroupParametric.visible = false;
    this.optimizedGroupParametric.add(this.assemblyOptimizedParametric);
    this.scene.add(this.optimizedGroupParametric);
  }

  // -- Drill bit delegates (implementation in DrillBitsManager) --
  generateSymmetryPlanes(maxSize: number, center: Vector3, size: Vector3): void { this.drillBitsManager.generateSymmetryPlanes(maxSize, center, size); }
  disposeDrillBits(): void { this.drillBitsManager.disposeDrillBits(); }
  disposeSymmetryPlanes(): void { this.drillBitsManager.disposeSymmetryPlanes(); }
  async initDrillBits(): Promise<void> { return this.drillBitsManager.initDrillBits(); }
  addDrillBitsToOutline(): void { this.drillBitsManager.addDrillBitsToOutline(); }
  removeDrillBitsFromOutline(): void { this.drillBitsManager.removeDrillBitsFromOutline(); }
  drillBitUpdate(): void { this.drillBitsManager.drillBitUpdate(); }
  scaleMeshToDiameter(mesh: Mesh, desiredDiameter: number): number { return this.drillBitsManager.scaleMeshToDiameter(mesh, desiredDiameter); }
  drillBitScaleChange(diameter: number): void { this.drillBitsManager.drillBitScaleChange(diameter); }

  createAndAnimateArrow(s: Vector3, d: Vector3, b: Box3, dist: number): void { this.helpers.createAndAnimateArrow(s, d, b, dist); }
  showArrow(): void { this.helpers.showArrow(); }
  hideArrow(): void { this.helpers.hideArrow(); }
  disposeArrow(): void { this.helpers.disposeArrow(); }

  private fpsMonitor = new FPSMonitor();
  private themeSubscription?: Subscription;

  get currentFPS(): number {
    return this.fpsMonitor.currentFPS;
  }

  animate(): void {
    this.animationFired = true;

    const now = performance.now();
    const delta = this.clock.getDelta();
    this.fpsMonitor.tick(now, delta * 1000);

    // Texture animation (throttled to 60fps for high refresh rate displays)
    if (this.animateTexture && now - this.lastTextureAnimTime >= this.textureAnimInterval) {
      this.lastTextureAnimTime = now;
      try {
        const mesh = this.session.engine?.infoGroup.children.find(
          (c) => c.userData['fea_type'] === this.session.engine?.currentFEAOutput
        ) as Mesh;
        if (mesh?.material instanceof ShaderMaterial) {
          mesh.material.uniforms['uTime'].value += 0.1;
        }
      } catch (e) {}
    }

    TWEEN.update();

    this.viewHelper.update(delta);

    this.orbitPivot.update();

    if (this.dimensionsHelper && this.dimensionsHelperVisible) {
      this.dimensionsHelper[0].update(this.camera);
      this.dimensionsHelper[1].update(this.camera);
      this.dimensionsHelper[2].update(this.camera);
    }

    this.jawsManager.updateMinGapBadgePosition();

    if (this.controls instanceof TrackballControls) {
      this.controls.handleResize();
      this.controls.update();
    }
    // Decoupled pivot persists through panning - no automatic clearing

    // if (this.clipper) this.clipper.performClipping();
    if (this.clipper) this.clipper.forEach((c) => c.performClipping());
    // if (this.clipperPrismatic) this.clipperPrismatic.performClipping();

    this.outlineEffect.update();

    this.renderer.clear();
    if (this.ssaoEnabled && this.composer) {
      this.composer.render(delta);
    } else {
      this.renderer.render(this.scene, this.camera);
    }

    this.viewHelper.render(this.renderer);
    this.renderer_css2d.render(this.scene, this.camera);
  }

  dispose(): void {
    // Stop animation loop
    this.renderer.setAnimationLoop(null);

    // Unsubscribe RxJS
    this.themeSubscription?.unsubscribe();

    // Remove TrackballControls event listeners
    this.controls.removeEventListener('start', this.orbitPivot.controlsActionStartHandler);
    this.controls.removeEventListener('end', this.orbitPivot.controlsActionEndHandler);
    this.controls.dispose?.();

    // Dispose manager resources
    this.keyboardShortcuts?.dispose();
    this.orbitPivot?.dispose();
    this.drillBitsManager?.disposeDrillBits();
    this.drillBitsManager?.disposeSymmetryPlanes();
    this.helpers?.disposeArrow();

    // Dispose DragControls on all parts
    for (const obj of this.objects) {
      const sphere = obj.group.userData['part_sphere'] as Mesh | undefined;
      const dragControl = sphere?.userData['dragControl'];
      if (dragControl?.dispose) {
        dragControl.dispose();
      }
    }

    // Dispose scene resources
    this.scene.traverse((child: Object3D) => {
      if (child instanceof Mesh) {
        child.geometry?.dispose();
        if (Array.isArray(child.material)) {
          (child.material as Material[]).forEach((m) => m.dispose());
        } else if (child.material) {
          (child.material as Material).dispose();
        }
      }
    });

    // Dispose grid
    this.disposeInfiniteGrid();

    // Dispose post-processing
    this.composer?.dispose();

    // Dispose renderers
    this.renderer_css2d?.domElement?.remove();
    this.renderer.dispose();

    // Clear references
    this.objects.length = 0;
    this.groups.length = 0;
  }

  // -- Load case visualization delegates (implementation in LoadCaseVisualizationManager) --
  toggleReactionForces(value?: boolean): void { this.loadCaseVisualization.toggleReactionForces(value); }
  hideReactionForces(): void { this.loadCaseVisualization.hideReactionForces(); }
  refreshReactionForceVisuals(): void { this.loadCaseVisualization.refreshReactionForceVisuals(); }
  toggleLoadCaseHighlight(value?: boolean): void { this.loadCaseVisualization.toggleLoadCaseHighlight(value); }
  positionGroupEditStack(group: LoadsGroupItem, force?: boolean): void { this.loadCaseVisualization.positionGroupEditStack(group, force); }

  createGroupEditStack(group: LoadsGroupItem): unknown { return this.loadCaseVisualization.createGroupEditStack(group); }
  disposeGroupEditStack(group: LoadsGroupItem): void { this.loadCaseVisualization.disposeGroupEditStack(group); }
  generateGroupEditStack(group: LoadsGroupItem): void { this.loadCaseVisualization.generateGroupEditStack(group); }
  setForceAndMomentWithManipulator(updateControl: boolean = false, group: LoadsGroupItem): void { this.loadCaseVisualization.setForceAndMomentWithManipulator(updateControl, group); }

  // Helpers
  get canvasWidth() {
    return (this.canvas.clientWidth * window.devicePixelRatio) | 0;
  }

  get canvasHeight() {
    return (this.canvas.clientHeight * window.devicePixelRatio) | 0;
  }

  get canvasAspect() {
    return this.canvasWidth / this.canvasHeight;
  }



  private static emptyBoundingBox(): { center: Vector3; size: Vector3; box: Box3 } {
    const d = DEFAULT_EMPTY_SIZE;
    return {
      center: new Vector3(0, 0, 0),
      size: new Vector3(d, d, d),
      box: new Box3(new Vector3(-d / 2, -d / 2, -d / 2), new Vector3(d / 2, d / 2, d / 2)),
    };
  }

  private static boxToInfo(box: Box3): { center: Vector3; size: Vector3; box: Box3 } {
    if (box.isEmpty()) return Engine.emptyBoundingBox();
    const center = new Vector3();
    const size = new Vector3();
    box.getCenter(center);
    box.getSize(size);
    return { center, size, box };
  }

  public calculateGroupMinMaxCenter(group: Group): { center: Vector3; size: Vector3; box: Box3 } {
    const min = new Vector3(Infinity, Infinity, Infinity);
    const max = new Vector3(-Infinity, -Infinity, -Infinity);
    const vertex = new Vector3();
    let hasVertices = false;

    group.traverse((child) => {
      if (
        child instanceof Mesh &&
        child.isMesh &&
        child.geometry instanceof BufferGeometry &&
        ('isMergedMesh' in child.userData || 'faceId' in child.userData)
      ) {
        const position = child.geometry.attributes.position;
        if (!position || !(position instanceof BufferAttribute)) return;

        for (let i = 0; i < position.count; i++) {
          hasVertices = true;
          vertex.fromBufferAttribute(position, i);
          vertex.applyMatrix4(child.matrixWorld);
          min.min(vertex);
          max.max(vertex);
        }
      }
    });

    if (!hasVertices) return Engine.emptyBoundingBox();

    const box = new Box3(min, max);
    const center = new Vector3().addVectors(min, max).multiplyScalar(0.5);
    const size = new Vector3().subVectors(max, min);
    return { center, size, box };
  }

  private _cachedObjectInfo!: { center: Vector3; size: Vector3; box: Box3 };
  private _cachedObjectInfoUpdateNeeded = true;
  public invalidateCachedObjectInfo(): void {
    this._cachedObjectInfoUpdateNeeded = true;
  }

  get objectInfo(): { center: Vector3; size: Vector3; box: Box3 } {
    this.pivot.updateWorldMatrix(true, true);
    this.group.updateWorldMatrix(true, true);

    if (
      this.session.project.projectType === ProjectType.SoftJaws ||
      this.session.project.userCoordinateSystem.enabled
    ) {
      if (this._cachedObjectInfoUpdateNeeded || !this._cachedObjectInfo) {
        this._cachedObjectInfoUpdateNeeded = false;
        this._cachedObjectInfo = this.calculateGroupMinMaxCenter(this.group);
      }
      return this._cachedObjectInfo;
    }

    return Engine.boxToInfo(new Box3().setFromObject(this.group));
  }

  getObjectBbox(object: Object3D): { center: Vector3; size: Vector3; box: Box3 } {
    return Engine.boxToInfo(new Box3().setFromObject(object));
  }

  applyThemeToGrid(): void { this.gridManager.applyTheme(); }

  /////////////////////////////// SOFTJAWS ///////////////////////////////
  public jaws: {
    fixed: {
      mesh?: Mesh;
      edges?: LineSegments;
    };
    movable: {
      mesh?: Mesh;
      edges?: LineSegments;
    };
    group: Group;
    result: Group;
    jawsPivot: Group;
    resources: Object3D[];
  } = {
    fixed: {
      mesh: undefined,
      edges: undefined
    },
    movable: {
      mesh: undefined,
      edges: undefined
    },
    group: new Group(),
    result: new Group(),
    jawsPivot: new Group(),
    resources: []
  };

  // ── Backward-compatible delegates to JawsManager ─────────────────────

  initJaws(): void { this.jawsManager.initJaws(); }
  updateJaws(): {
    effectiveJawWall: number;
    effectiveJawGap: number;
    atMinGap: boolean;
  } | undefined { return this.jawsManager.updateJaws(); }
  async addJawsResult(resultProtoUrl: string): Promise<void> { return this.jawsManager.addJawsResult(resultProtoUrl); }
  removeJawsResult(): void { this.jawsManager.removeJawsResult(); }

  /**
   * Add a generated shape part to the engine
   * @param protoData - ArrayBuffer containing the proto data
   * @param partId - The ID of the part to add
   * @param partName - The name of the part
   */
  async addGeneratedShapePart(protoData: ArrayBuffer, partId: string, partName: string): Promise<void> {
    const decoded = await this.decodeAssemblyProto(protoData);

    // Find the specific part in the assembly
    const partData = decoded.parts?.find((p: ProtoPart) => p.id === partId);
    if (!partData) {
      console.warn(`Part ${partId} not found in assembly proto`);
      return;
    }

    const group = new Group();
    group.name = partName;
    group.userData = { id: partId, pid: partData.parentId, source: 'shape' };

    // Build the part as a single merged mesh with PerFaceMaterial — same structure
    // as initMesh, so design/non-design coloring, face picking, and every other
    // merged-mesh-aware path works identically for imported parts.
    const cutOffAngle = GENERATED_SHAPE_CUTOFF_ANGLE;

    const processedFaces = await Promise.all(
      (partData.faces || []).map(async (face: ProtoFace) => {
        const geometry = await this.processFaceGeometry(
          new Float32Array(face.vertices),
          new Uint32Array(face.indices),
          cutOffAngle
        );
        return { geometry, faceId: face.id };
      })
    );

    const faceGeometries: BufferGeometry[] = [];
    const faces: string[] = [];
    for (const { geometry, faceId } of processedFaces) {
      faceGeometries.push(geometry);
      faces.push(faceId);
    }

    const partColor = partData.color || '#6366f1';
    const { geometry: mergedGeometry, faceIdToIndex } = mergeGeometriesWithFaceIndex(faceGeometries, faces);

    const perFaceMaterial = new PerFaceMaterial(
      {
        color: partColor,
        side: DoubleSide,
        vertexColors: false,
        flatShading: false,
        opacity: 1,
        transparent: false,
        metalness: 0.7,
        reflectivity: 0.6,
        roughness: 0.5,
        clearcoat: 1,
        clearcoatRoughness: OPTIMIZED_CLEARCOAT_ROUGHNESS,
        envMap: this.envMap,
        envMapIntensity: 0.4,
        clippingPlanes: this.clippingPlanes
      },
      faces,
      partColor
    );

    const meshobj = new Mesh(mergedGeometry, perFaceMaterial);
    this.computeBVHAsync(meshobj.geometry).catch(() => {});
    meshobj.name = meshobj.uuid;
    meshobj.userData = {
      id: partId,
      parentId: partData.parentId,
      originalColor: partColor,
      faceIds: faces,
      faceIdToIndex: faceIdToIndex,
      perFaceMaterial: perFaceMaterial,
      isMergedMesh: true
    };
    group.add(meshobj);

    const wirematerial = this.createWireLineMaterial();

    const { wiresGroup, wireNames: wires } = this.createMergedWireGroup(partData.wires || [], wirematerial, 'AssemblyMeshLine');
    group.add(wiresGroup);

    this.createPartHelpers(group);

    // Add to scene
    this.group.add(group);
    this.groups.push(group);

    // Add to objects list
    this.objects.push({
      group: group,
      data: partData,
      faces: faces,
      wires: wires,
      wirematerial: wirematerial
    });

    // Sort objects by name
    this.objects.sort((a, b) => {
      if (a.data.name < b.data.name) return -1;
      if (a.data.name > b.data.name) return 1;
      return 0;
    });

    // Update world matrices so COM/center calculations are correct
    this.pivot.updateMatrixWorld(true);

    // Recalculate bounds and update scene
    this.invalidateCachedObjectInfo();
    const { center, size } = this.objectInfo;
    this.controls.target.copy(center);
    this.gridHelper = Controls.createGridHelper(this.gridHelper, size, this.scene, this.canvas, center);
    if (this.useInfiniteGrid && this.gridHelper) {
      this.gridHelper.visible = false;
      this.gridHelper.userData['xLine'].visible = false;
      this.gridHelper.userData['yLine'].visible = false;
      this.gridHelper.userData['groundPlane'].visible = false;
    }
    this.createInfiniteGrid(size, center);
    this.applyThemeToGrid();
    this.dimensionsHelper = Controls.createDimensionsHelper(this, this.objectInfo.box);
    this.toggleDimensionsHelper(true);
    this.windowResize();
  }

  /**
   * Removes a part from the scene by its ID
   * @param partId - The ID of the part to remove
   * @returns true if part was found and removed, false otherwise
   */
  removePartById(partId: string): boolean {
    const objectIndex = this.objects.findIndex((o) => o.data.id === partId);
    if (objectIndex === -1) {
      console.warn(`Part ${partId} not found in engine objects`);
      return false;
    }

    const obj = this.objects[objectIndex];
    
    // Remove from outline selection if present
    obj.group.children.forEach((child: Object3D) => {
      if (child instanceof Mesh) {
        this.outlineEffect.selection.delete(child);
      }
    });

    // Dispose of geometries and materials
    obj.group.traverse((child: Object3D) => {
      if ((child as Mesh).geometry) {
        (child as Mesh).geometry.dispose();
      }
      if ((child as Mesh).material) {
        if (Array.isArray((child as Mesh).material)) {
          ((child as Mesh).material as Material[]).forEach((m: Material) => m.dispose());
        } else {
          ((child as Mesh).material as Material).dispose();
        }
      }
    });

    // Remove from scene group
    this.group.remove(obj.group);
    
    // Remove from groups array
    const groupIndex = this.groups.indexOf(obj.group);
    if (groupIndex !== -1) {
      this.groups.splice(groupIndex, 1);
    }

    // Remove from objects array
    this.objects.splice(objectIndex, 1);

    // Update world matrices and recalculate bounds
    this.pivot.updateMatrixWorld(true);
    this.invalidateCachedObjectInfo();
    const { center, size } = this.objectInfo;
    this.controls.target.copy(center);
    this.gridHelper = Controls.createGridHelper(this.gridHelper, size, this.scene, this.canvas, center);
    if (this.useInfiniteGrid && this.gridHelper) {
      this.gridHelper.visible = false;
      this.gridHelper.userData['xLine'].visible = false;
      this.gridHelper.userData['yLine'].visible = false;
      this.gridHelper.userData['groundPlane'].visible = false;
    }
    this.createInfiniteGrid(size, center);
    this.applyThemeToGrid();
    this.dimensionsHelper = Controls.createDimensionsHelper(this, this.objectInfo.box);
    this.toggleDimensionsHelper(true);
    this.windowResize();

    return true;
  }

  /**
   * Removes multiple parts from the scene by their IDs
   * @param partIds - Array of part IDs to remove
   * @returns Array of IDs that were successfully removed
   */
  removePartsById(partIds: string[]): string[] {
    const removedIds: string[] = [];
    for (const partId of partIds) {
      if (this.removePartById(partId)) {
        removedIds.push(partId);
      }
    }
    return removedIds;
  }

  /**
   * Reloads and syncs parts from an updated assembly proto
   * Adds new parts that don't exist and keeps existing ones
   * @param protoData - The new proto data as ArrayBuffer
   * @returns Object containing arrays of added part IDs/names
   */
  async syncPartsFromProto(protoData: ArrayBuffer): Promise<{ addedParts: { id: string; name: string }[] }> {
    const decoded = await this.decodeAssemblyProto(protoData);
    const existingIds = new Set(this.objects.map(o => o.data.id));
    const addedParts: { id: string; name: string }[] = [];
    
    // Add parts that don't exist yet
    for (const partData of decoded.parts || []) {
      if (!existingIds.has(partData.id)) {
        await this.addGeneratedShapePart(protoData, partData.id, partData.name);
        addedParts.push({ id: partData.id, name: partData.name });
      }
    }

    if (addedParts.length > 0) {
      this.session.updateObjectTypes();
    }

    return { addedParts };
  }

  checkJawCollisions(): { fixedJawIntersects: boolean; movableJawIntersects: boolean } { return this.jawsManager.checkJawCollisions(); }
  updateJawColors(fixedIntersects: boolean, movableIntersects: boolean): void { this.jawsManager.updateJawColors(fixedIntersects, movableIntersects); }
  rotateModelToFace(faceNormal: Vector3, onComplete: (modelRotation: { x: number; y: number; z: number }) => void): void { this.jawsManager.rotateModelToFace(faceNormal, onComplete); }
  enableRotationGizmo(onChange: (rotation: { x: number; y: number; z: number }) => void): void { this.jawsManager.enableRotationGizmo(onChange); }
  disableRotationGizmo(): void { this.jawsManager.disableRotationGizmo(); }
  updateRotationGizmo(): void { this.jawsManager.updateRotationGizmo(); }

  // ═══════════════════════════════════════════════════════════════════════════
  // CAD EDITOR VISIBILITY MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════════

  // ── Backward-compatible delegates to CADEditorVisibilityManager ─────
  hideAllObjectsForCADEditor(): void { this.cadEditorVisibility.hideAll(); }
  restoreAllObjectsForCADEditor(): void { this.cadEditorVisibility.restoreAll(); }
}
export { Engine };
